// --- Dependencies -------------------------------------------------------------------------------
import { useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import Icon from "@/UI/atoms/Icon";
import MultiSelectInput from "@/UI/inputs/MultiSelectInput";
import NumberInput from "@/UI/inputs/NumberInput";
import SelectInput from "@/UI/inputs/SelectInput";
import TextInput from "@/UI/inputs/TextInput";
import BulkReviewPopup, { type TyBulkChange } from "@/UI/popups/BulkReviewPopup";
import type { TyBulkBarProps, TyBulkField } from "./types";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
/**
 * The sentinel for "leave this field alone", which is not a value any field can hold. A field is in
 * the draft once it has been touched and out of it otherwise, so an untouched dropdown writes nothing
 * — and `""`, which *is* a real answer, means clear it.
 */
const KEEP = "__keep__";

/**
 * The bar that rises when rows are picked, **in the paginator's place and not beside it**.
 *
 * One of the two is on screen, never both, which is a layout choice carrying a rule: a selection is
 * page-scoped, and a pager left available would let somebody walk away from the rows they are about
 * to write to. Taking it away for the duration says so without a sentence of explanation.
 *
 * The fields scroll sideways and the buttons do not — save, delete and cancel are stuck to the end,
 * because the one thing that must never scroll out of reach is the way out of this mode.
 *
 * **Nothing is written without being read back.** The draft sits here until `Save`, and `Save` opens
 * the review popup rather than the request: a person who has scrolled a dozen fields sideways has
 * not seen the three they filled in together, and the three together are what goes to eighty records.
 */
export default function BulkBar({ count, fields, onApply, extras, onDelete, deleteLabel, onClear, busy, note }: TyBulkBarProps) {
    const t = useTranslation();
    const addComp = usePopup((state) => state.addComp);
    const [draft, setDraft] = useState<Record<string, unknown>>({});

    const write = (key: string, value: unknown) =>
        setDraft((current) => {
            const next = { ...current };
            if (value === KEEP || value === undefined) delete next[key];
            else next[key] = value;
            return next;
        });

    /** A field whose `when` has stopped holding leaves the row, and leaves the write with it. */
    const shown = (fields ?? []).filter((field) => !field.when || field.when(draft));
    const touched = shown.filter((field) => field.key in draft);

    /**
     * Only what is still on screen. Choosing rent, typing a period and then choosing sale would
     * otherwise send a rent period nobody can see any more — harmless where the API happens to drop
     * it, and a field written by accident wherever it does not.
     *
     * A dotted key — `measures.builtArea`, `choices.acSystem` — is nested on the way out, because the
     * API whitelists the **group** and reads the one field inside it. Flat, it would be refused; sent
     * as a whole group object, it would wipe the two areas nobody mentioned.
     */
    const payload = () => {
        const out: Record<string, unknown> = {};

        for (const field of touched) {
            const [group, leaf] = field.key.split(".");
            if (leaf === undefined) out[field.key] = draft[field.key];
            else {
                const nested = (out[group] ??= {}) as Record<string, unknown>;
                nested[leaf] = draft[field.key];
            }
        }

        return out;
    };

    /** The draft in words, which is what the review popup reads back and the group buttons show. */
    const describe = (field: TyBulkField): string => {
        const value = draft[field.key];

        if (field.kind === "group") return field.brief?.(value) ?? t.bulk.written;
        if (field.kind === "check") {
            const [yes, no] = field.options ?? [];
            return value ? (yes?.label ?? t.properties.yes) : (no?.label ?? t.properties.no);
        }
        if (field.kind === "select") return field.options?.find((option) => option.value === String(value))?.label ?? t.properties.notSet;
        if (field.kind === "multi") {
            const picked = Array.isArray(value) ? value : [];
            if (!picked.length) return t.bulk.groupCleared;
            return picked.map((one) => field.options?.find((option) => option.value === one)?.label ?? one).join(" · ");
        }
        if (value === "" || value === null) return t.properties.notSet;
        return `${value}${field.suffix ? ` ${field.suffix}` : ""}`;
    };

    const review = () => {
        if (!touched.length || !onApply) return;
        const changes: TyBulkChange[] = touched.map((field) => ({ key: field.key, label: field.label, value: describe(field) }));

        addComp({
            name: "bulk-review",
            Component: BulkReviewPopup,
            anim: "scale",
            details: { count, changes, onConfirm: () => onApply(payload()) },
        });
    };

    return (
        <div className="bulk-bar" role="region" aria-label={t.bulk.selected.replace("{count}", String(count))}>
            <div className="bulk-bar__bar">
                <span className="bulk-bar__count">
                    <strong className="bulk-bar__figure">{count}</strong>
                    {t.bulk.selected.replace("{count}", "").trim()}
                </span>

                {!!shown.length && <span className="bulk-bar__divider" />}

                <div className="bulk-bar__fields">
                    {shown.map((field) => {
                        const value = draft[field.key];

                        /* Too big for a bar, so the bar keeps the value and a popup does the editing. */
                        if (field.kind === "group") {
                            const isSet = field.key in draft;
                            return (
                                <span key={field.key} className={`bulk-bar__group${isSet ? " bulk-bar__group--on" : ""}`}>
                                    <button
                                        type="button"
                                        className="bulk-bar__group-open"
                                        onClick={() => field.open?.(value, (next) => write(field.key, next))}
                                        disabled={busy}
                                    >
                                        <span className="bulk-bar__group-label">{field.label}</span>
                                        <span className="bulk-bar__group-brief">{isSet ? describe(field) : t.bulk.keep}</span>
                                    </button>

                                    {isSet && (
                                        <button
                                            type="button"
                                            className="bulk-bar__group-drop"
                                            onClick={() => write(field.key, KEEP)}
                                            disabled={busy}
                                            aria-label={t.bulk.keep}
                                            title={t.bulk.keep}
                                        >
                                            <Icon name="close" size={11} />
                                        </button>
                                    )}
                                </span>
                            );
                        }

                        /**
                         * A flag, as the box it is. **Three states, two controls**: the box answers
                         * yes or no, and the × beside it goes back to not writing the field at all —
                         * which an unticked box cannot say, since an unticked box *is* "no".
                         *
                         * It sizes to its own label rather than to the bar's column, because "Use
                         * the project's gallery" is a sentence and a 120px box would cut it in half.
                         */
                        if (field.kind === "check") {
                            const isSet = field.key in draft;
                            return (
                                <span key={field.key} className={`bulk-bar__check${isSet ? " bulk-bar__check--on" : ""}`}>
                                    <label className="bulk-bar__check-label">
                                        <input
                                            type="checkbox"
                                            className="bulk-bar__check-box"
                                            checked={!!value}
                                            disabled={busy}
                                            onChange={(event) => write(field.key, event.target.checked)}
                                        />
                                        <span className="bulk-bar__check-text">{field.label}</span>
                                    </label>

                                    {isSet && (
                                        <button
                                            type="button"
                                            className="bulk-bar__check-drop"
                                            onClick={() => write(field.key, KEEP)}
                                            disabled={busy}
                                            aria-label={t.bulk.keep}
                                            title={t.bulk.keep}
                                        >
                                            <Icon name="close" size={11} />
                                        </button>
                                    )}
                                </span>
                            );
                        }

                        if (field.kind === "select") {
                            const box = (
                                <SelectInput
                                    compact
                                    label={field.label}
                                    value={value === undefined ? KEEP : String(value)}
                                    onChange={(next) => write(field.key, next)}
                                    disabled={busy}
                                    options={[{ value: KEEP, label: t.bulk.keep }, ...(field.options ?? [])]}
                                />
                            );

                            if (!field.onAdd)
                                return (
                                    <span key={field.key} className="bulk-bar__field">
                                        {box}
                                    </span>
                                );

                            /* A block can be made from here, and is chosen the moment it exists. */
                            return (
                                <span key={field.key} className="bulk-bar__field bulk-bar__field--with-add">
                                    {box}
                                    <button
                                        type="button"
                                        className="bulk-bar__add"
                                        onClick={() => field.onAdd?.((next) => write(field.key, next))}
                                        disabled={busy}
                                        aria-label={field.addLabel ?? t.common.add}
                                        title={field.addLabel ?? t.common.add}
                                    >
                                        <Icon name="add" size={14} />
                                    </button>
                                </span>
                            );
                        }

                        /**
                         * Typed and picked fields need a way **out** of the write, which a select
                         * gets from its "leave unchanged" option and a toggle from its third mark.
                         * Emptying a box is not it: on a bulk write, empty is a real instruction —
                         * clear the price on forty units — and quite different from not touching it.
                         */
                        const drop = field.key in draft && (
                            <button
                                type="button"
                                className="bulk-bar__drop"
                                onClick={() => write(field.key, KEEP)}
                                disabled={busy}
                                aria-label={t.bulk.keep}
                                title={t.bulk.keep}
                            >
                                <Icon name="close" size={10} />
                            </button>
                        );

                        if (field.kind === "multi") {
                            return (
                                <span key={field.key} className="bulk-bar__field">
                                    <MultiSelectInput
                                        compact
                                        label={field.label}
                                        disabled={busy}
                                        value={Array.isArray(value) ? (value as string[]) : []}
                                        onChange={(next) => write(field.key, next)}
                                        options={field.options ?? []}
                                    />
                                    {drop}
                                </span>
                            );
                        }

                        if (field.kind === "number") {
                            return (
                                <span key={field.key} className="bulk-bar__field">
                                    <NumberInput
                                        compact
                                        label={field.label}
                                        suffix={field.suffix}
                                        disabled={busy}
                                        value={value === undefined || value === "" ? null : Number(value)}
                                        // Emptying a field that was typed into means **clear it**,
                                        // which is why `""` is kept in the draft rather than removed.
                                        onChange={(next) => write(field.key, next === null ? "" : next)}
                                    />
                                    {drop}
                                </span>
                            );
                        }

                        return (
                            <span key={field.key} className="bulk-bar__field">
                                <TextInput
                                    compact
                                    label={field.label}
                                    disabled={busy}
                                    value={value === undefined ? "" : String(value)}
                                    onChange={(next) => write(field.key, next)}
                                />
                                {drop}
                            </span>
                        );
                    })}
                </div>

                <span className="bulk-bar__divider" />

                {/* Stuck to the end: the way out of this mode must never scroll away. */}
                <div className="bulk-bar__acts">
                    {onApply && (
                        <button
                            type="button"
                            className="bulk-bar__save"
                            onClick={review}
                            disabled={!touched.length || busy}
                            title={touched.length ? undefined : t.bulk.hint}
                        >
                            <Icon name="check" size={15} />
                            <span>{t.common.save}</span>
                        </button>
                    )}

                    {(extras ?? []).map((action) => (
                        <button key={action.key} type="button" className="bulk-bar__extra" onClick={action.onSelect} disabled={busy}>
                            <Icon name={action.icon} size={15} />
                            <span>{action.label}</span>
                        </button>
                    ))}

                    {onDelete && (
                        <button type="button" className="bulk-bar__delete" onClick={onDelete} disabled={busy}>
                            <Icon name="trash" size={15} />
                            <span>{deleteLabel ?? t.common.remove}</span>
                        </button>
                    )}

                    <button type="button" className="bulk-bar__clear" onClick={onClear} disabled={busy} aria-label={t.bulk.exit} title={t.bulk.exit}>
                        <Icon name="close" size={13} />
                    </button>
                </div>
            </div>

            {!!note && <p className="bulk-bar__note">{note}</p>}
        </div>
    );
}
